/**
 * LocalStorageAdapter - Storage para preferencias pequeñas
 *
 * Usa localStorage del navegador.
 * Ideal para: preferences, pequeños flags, settings simples
 *
 * Limitaciones:
 * - Solo strings (se usa JSON.stringify/parse)
 * - ~5-10MB límite total
 * - Síncrono (pero lo hacemos async para consistencia)
 */

import { BaseAdapter } from './BaseAdapter';

const PREFIX = 'xhelos_';

export class LocalStorageAdapter extends BaseAdapter {
  constructor(namespace = 'prefs') {
    super('LocalStorageAdapter');
    this.namespace = namespace;
    this.prefix = `${PREFIX}${namespace}_`;
  }

  /**
   * Inicializa (localStorage no requiere init, pero mantenemos consistencia)
   */
  async init() {
    if (this.initialized) return;

    // Verificar disponibilidad de localStorage
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      this.initialized = true;
    } catch (error) {
      console.warn(`[LocalStorageAdapter] localStorage not available:`, error);
      this.initialized = true; // Marcamos como init pero operaciones fallarán silenciosamente
    }
  }

  /**
   * Genera key con prefix
   */
  _toKey(key) {
    return `${this.prefix}${key}`;
  }

  /**
   * Extrae key sin prefix
   */
  _fromKey(fullKey) {
    if (fullKey.startsWith(this.prefix)) {
      return fullKey.slice(this.prefix.length);
    }
    return fullKey;
  }

  /**
   * Verifica si localStorage está disponible
   */
  isAvailable() {
    try {
      return typeof localStorage !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Verifica si existe un key
   */
  async has(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return false;

    try {
      return localStorage.getItem(this._toKey(key)) !== null;
    } catch (error) {
      console.warn(`[LocalStorageAdapter] has() error:`, error);
      return false;
    }
  }

  /**
   * Obtiene un valor (deserializa JSON)
   */
  async get(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return null;

    try {
      const raw = localStorage.getItem(this._toKey(key));
      if (raw === null) return null;

      // Intentar parsear como JSON
      try {
        return JSON.parse(raw);
      } catch {
        // Si no es JSON válido, retornar como string
        return raw;
      }
    } catch (error) {
      console.warn(`[LocalStorageAdapter] get() error:`, error);
      return null;
    }
  }

  /**
   * Guarda un valor (serializa a JSON)
   */
  async set(key, value, options = {}) {
    this.ensureInitialized();
    if (!this.isAvailable()) {
      console.warn(`[LocalStorageAdapter] localStorage not available, cannot save ${key}`);
      return;
    }

    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(this._toKey(key), serialized);
    } catch (error) {
      // QuotaExceededError si localStorage está lleno
      console.error(`[LocalStorageAdapter] set() error:`, error);
      throw error;
    }
  }

  /**
   * Elimina un key
   */
  async delete(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      localStorage.removeItem(this._toKey(key));
    } catch (error) {
      console.warn(`[LocalStorageAdapter] delete() error:`, error);
    }
  }

  /**
   * Limpia todos los keys de este namespace
   */
  async clear() {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn(`[LocalStorageAdapter] clear() error:`, error);
    }
  }

  /**
   * Obtiene todas las keys de este namespace
   */
  async keys() {
    this.ensureInitialized();
    if (!this.isAvailable()) return [];

    try {
      const keys = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keys.push(this._fromKey(key));
        }
      }

      return keys;
    } catch (error) {
      console.warn(`[LocalStorageAdapter] keys() error:`, error);
      return [];
    }
  }

  /**
   * Obtiene estadísticas
   */
  async getStats() {
    const allKeys = await this.keys();
    let totalSize = 0;

    for (const key of allKeys) {
      const value = localStorage.getItem(this._toKey(key));
      if (value) {
        totalSize += value.length * 2; // UTF-16 = 2 bytes por char
      }
    }

    return {
      count: allKeys.length,
      size: totalSize,
      sizeFormatted: `${(totalSize / 1024).toFixed(2)}KB`,
    };
  }
}

export default LocalStorageAdapter;
